import { useEffect, useState } from 'react';

import { Image } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useDesignerApp } from '../../../UserInterfaceDesigner/Contexts/DesignerAppContext';
import { Bundle } from '../../../UserInterfaceDesigner/Models/Bundle';
import { EmbeddedResourceModel } from '../../../UserInterfaceDesigner/Models/EmbeddedResourceModel';
import { EmbeddedResourceRefModel } from '../../Models/EmbeddedResourceRefModel';
import ListWithFilter from '../ListWithFilter';

type EmbeddedResourceInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	embeddedResourceRef: EmbeddedResourceRefModel | undefined;
	onEmbeddedResourceRefChange: (embeddedResourceRef: EmbeddedResourceRefModel | undefined) => void;
	/** Bundle with embedded resources (from context or prop) */
	bundle?: Bundle | null;
};

export function EmbeddedResourceInputDialog({ open, setOpen, embeddedResourceRef, onEmbeddedResourceRefChange, bundle }: EmbeddedResourceInputDialogProps) {
	const [selectedId, setSelectedId] = useState<string | undefined>(embeddedResourceRef?.Id);
	const designerBundle = useDesignerApp();

	// Use provided bundle, or fall back to designer context bundle
	const activeBundle = bundle ?? designerBundle.bundle;
	const embeddedResources: EmbeddedResourceModel[] = activeBundle?.EmbeddedResources || [];

	useEffect(() => {
		if (open) {
			setSelectedId(embeddedResourceRef?.Id);
		}
	}, [open, embeddedResourceRef]);

	const handleSelect = (resource: EmbeddedResourceModel) => {
		setSelectedId(resource.Id);
	};

	const handleSave = () => {
		if (selectedId) {
			const resource = embeddedResources.find((r: EmbeddedResourceModel) => r.Id === selectedId);
			onEmbeddedResourceRefChange({
				Id: selectedId,
				Name: resource?.Name,
			});
		}
		setOpen(false);
	};

	return (
		<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
			<DialogTitle>Select Embedded Resource</DialogTitle>
			<DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
				{embeddedResources.length > 0 ? (
					<ListWithFilter
						items={embeddedResources}
						selectedItemId={selectedId}
						onSelected={handleSelect}
						placeholder="Search embedded resources"
						getDisplayName={(resource) => resource.Name}
						getKey={(resource) => resource.Id}
						icon={() => <Image fontSize="small" />}
						settingsKey="embeddedResourceInputDialog"
						sx={{ flex: 1, minHeight: 0 }}
					/>
				) : (
					<Box sx={{ p: 2, textAlign: 'center' }}>
						<Typography color="text.secondary">No embedded resources available. Please add resources to the bundle first.</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>Cancel</Button>
				<Button onClick={handleSave} variant="contained" disabled={!selectedId}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
